/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import classNames from 'classnames';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { IconButton } from '@coursera/cds-core';
import { AddIcon, ArrowUpIcon, MicrophoneIcon, StopFilledIcon } from '@coursera/cds-icons';

import ChatContextAttachment from 'bundles/cds-labs/components/chat/ChatContextAttachment';
import type { ChatResponseFieldRef } from 'bundles/cds-labs/components/chat/types';
import useSpeechToText, {
  SpeechRecognitionStatus,
  type SpeechToTextError,
} from 'bundles/cds-labs/components/chat/useSpeechToText';

import _t from 'i18n!nls/cds-labs';

import getChatResponseFieldCss, {
  DEFAULT_MAX_ROWS,
  LINE_HEIGHT_PX,
  SINGLE_LINE_HEIGHT,
  TEXTAREA_VERTICAL_PADDING_PX,
  classes,
} from './getChatResponseFieldCss';

export type { SpeechToTextError };

export type { ChatResponseFieldRef };

type AttachmentPreview = {
  id: string;
  file: File;
  /** Object URL for image files; undefined for non-image files. */
  previewUrl: string | undefined;
};

// Applied to preview <img> elements inside the media attachment slot.
const styles = {
  previewImage: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  `,
  scaledIcon: css`
    display: flex;
    transform: scale(1.4);
  `,
};

function matchesAccept(file: File, accept: string): boolean {
  return accept.split(',').some((token) => {
    const t = token.trim().toLowerCase();
    if (t.startsWith('.')) return file.name.toLowerCase().endsWith(t);
    if (t.endsWith('/*')) return file.type.startsWith(t.slice(0, -1));
    return file.type === t;
  });
}

export type ChatResponseFieldProps = {
  /** Controlled value. Mutually exclusive with `defaultValue`. */
  value?: string;
  /** Initial value for uncontrolled usage. Mutually exclusive with `value`. */
  defaultValue?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onSend?: (value: string) => void;
  /**
   * When provided, an attachment button is shown and paste-to-attach is enabled.
   * Called with the full current list of attached files whenever it changes —
   * on add (via file picker, paste, or drag-and-drop) and on remove.
   *
   * @migration Renamed from `onUpload: (files: FileList) => void`. The new
   * signature uses `File[]` instead of `FileList` and fires on removal too.
   */
  onAttachmentsChange?: (files: File[]) => void;
  /** Passed as the `accept` attribute on the hidden file input. E.g. `"image/*"` or `".pdf,.docx"`. */
  accept?: string;
  leadingAction?: React.ReactNode;
  contextAttachments?: React.ReactNode;
  trailingActions?: React.ReactNode;
  isStreaming?: boolean;
  onStop?: () => void;
  maxLength?: number;
  /** Maximum number of visible rows before the textarea starts scrolling. @default DEFAULT_MAX_ROWS */
  maxRows?: number;
  disabled?: boolean;
  /** When true, the textarea stays editable but submission actions are disabled. */
  disableSubmit?: boolean;
  autoFocus?: boolean;
  'aria-label'?: string;
  /**
   * When true, the send button is enabled and `onSend` fires even if the
   * input text is empty.
   */
  allowEmptySend?: boolean;
  /** Called when the microphone starts listening. */
  onMicrophoneStart?: () => void;
  /** Called when the microphone stops listening (user action or auto-stop after silence). */
  onMicrophoneStop?: () => void;
  /**
   * Called on every speech recognition result — both interim previews and
   * committed final transcripts. Mirrors the `onTranscript` callback of
   * `useSpeechToText`; useful for tracking dictation attempts in callers.
   */
  onTranscript?: (transcript: string, isInterim: boolean) => void;
  /** Called when the Web Speech API reports an error (e.g. `'not-allowed'`). */
  onMicrophoneError?: (error: SpeechToTextError) => void;
  /** When true, the microphone button is shown in a disabled state. */
  disableSpeechToText?: boolean;
  /** Tooltip shown on the disabled microphone button. Only used when `disableSpeechToText` is true. */
  speechToTextDisabledTooltip?: string;
  /** When true, the microphone button is hidden entirely even if the browser supports speech recognition. */
  hideSpeechToTextButton?: boolean;
  'data-testid'?: string;
  /**
   * When true, always uses the stacked layout (leading action and controls in a row below
   * the textarea)
   */
  multiLineLayout?: boolean;
};

const ChatResponseField = React.forwardRef<ChatResponseFieldRef, ChatResponseFieldProps>(
  function ChatResponseField(props, ref) {
    const {
      value,
      defaultValue,
      placeholder,
      onChange,
      onSend,
      onAttachmentsChange,
      accept,
      leadingAction,
      contextAttachments,
      trailingActions,
      isStreaming = false,
      onStop,
      maxLength,
      maxRows = DEFAULT_MAX_ROWS,
      disabled = false,
      disableSubmit = false,
      autoFocus = false,
      'aria-label': ariaLabel = _t('Chat message input'),
      allowEmptySend = false,
      onMicrophoneStart,
      onMicrophoneStop,
      onTranscript,
      onMicrophoneError,
      disableSpeechToText = false,
      speechToTextDisabledTooltip,
      hideSpeechToTextButton = false,
      'data-testid': dataTestId = 'chat-response-field',
      multiLineLayout = false,
    } = props;

    const maxTextareaHeight = LINE_HEIGHT_PX * maxRows + TEXTAREA_VERTICAL_PADDING_PX;

    const instanceId = useId();
    const maxLengthDescriptionId = maxLength !== undefined ? `${instanceId}-maxlength` : undefined;

    const formRef = useRef<HTMLFormElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textWidthMeasureRef = useRef<HTMLSpanElement>(null);
    // Last known textarea clientWidth while in single-line mode; used to keep
    // multi-line stable when the textarea widens after layout switches.
    const singleLineWidthRef = useRef(0);
    const [isMultiLine, setIsMultiLine] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    // Tracks the textarea value in uncontrolled mode so canSend / isOverMaxLength
    // stay in sync with what the user typed without a consumer-managed value prop.
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
    // STT interim transcript shown as a preview suffix while the user is speaking.
    const [interimTranscript, setInterimTranscript] = useState('');
    // Ref so event handlers always see the current listening state without stale closures.
    const listeningRef = useRef(false);
    const stopListeningRef = useRef<() => void>(() => {});
    const [attachmentPreviews, setAttachmentPreviews] = useState<AttachmentPreview[]>([]);
    // Debounced char-limit count for screen-reader announcements — mirrors the CDS
    // CharacterLimit component pattern. The visual span always shows the live count
    // (aria-hidden) while this state drives a live region that only updates after the
    // user pauses typing, so the screen reader can complete each announcement without
    // being cut off mid-word by rapid keystrokes.
    const [announcedRemaining, setAnnouncedRemaining] = useState<number | undefined>(undefined);
    const charLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingCharLimitRef = useRef(false);
    const prevRemainingRef = useRef<number | undefined>(undefined);
    const latestRemainingRef = useRef<number | undefined>(undefined);
    const isCharLimitMountRef = useRef(true);
    // Ref-tracked so the cleanup effect (empty deps) can revoke all remaining URLs,
    // and so attachment handlers can read the current list without stale closures.
    const createdUrlsRef = useRef<string[]>([]);
    const attachmentPreviewsRef = useRef<AttachmentPreview[]>([]);
    const attachmentIdCounterRef = useRef(0);
    attachmentPreviewsRef.current = attachmentPreviews;
    // Maps attachment IDs to their root DOM nodes so the focus effect can find the button.
    const attachmentDomRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    // Set in handleFileInputChange to the first new attachment's ID; cleared after focusing.
    const pendingFocusAttachmentId = useRef<string | null>(null);

    // Derived values — computed early so they are available in effects below.
    const currentValue = value ?? uncontrolledValue;
    const isOverMaxLength = maxLength !== undefined && currentValue.length > maxLength;
    const canSend =
      !isStreaming &&
      !disabled &&
      !disableSubmit &&
      !isOverMaxLength &&
      (allowEmptySend || currentValue.trim().length > 0);
    const remaining = maxLength !== undefined ? maxLength - currentValue.length : undefined;
    // Always keep the ref current so the debounced SR callback announces the latest value.
    latestRemainingRef.current = remaining;

    // Ref-tracked so handleTranscript can read the latest committed value without
    // listing currentValue as a useCallback dep. Without this, every interim result
    // would recreate handleTranscript → retrigger useSpeechToText's recognition
    // event-setup effect → replace all recognition.on* handlers mid-speech.
    const currentValueRef = useRef(currentValue);
    currentValueRef.current = currentValue;

    // STT transcript handler — called by useSpeechToText on every result event.
    // Height recalculation is handled by the effect that watches interimTranscript /
    // uncontrolledValue, so no direct updateHeight call is needed here.
    const handleTranscript = useCallback(
      (transcript: string, isInterim: boolean) => {
        onTranscript?.(transcript, isInterim);
        if (isInterim) {
          setInterimTranscript(transcript);
        } else {
          const next = currentValueRef.current + transcript;
          setInterimTranscript('');
          if (value === undefined) {
            setUncontrolledValue(next);
          }
          onChange?.(next);
        }
      },
      [onChange, onTranscript, value]
    );

    const handleMicStart = useCallback(() => {
      listeningRef.current = true;
      onMicrophoneStart?.();
    }, [onMicrophoneStart]);

    const handleMicStop = useCallback(() => {
      // Guard against double-fire: called both by the explicit stopListening() path
      // and by recognition.onend for any termination (OS interrupt, tab focus change).
      if (!listeningRef.current) return;
      listeningRef.current = false;
      setInterimTranscript('');
      onMicrophoneStop?.();
    }, [onMicrophoneStop]);

    const {
      startListening,
      stopListening,
      status,
      listening,
      error: sttError,
      isSupportedBrowser,
      isSupportedLanguage,
    } = useSpeechToText({
      stopAfterSeconds: 4,
      onTranscript: handleTranscript,
      onStartListening: handleMicStart,
      onStopListening: handleMicStop,
      onError: onMicrophoneError,
    });

    // Keep stopListeningRef current so handleSubmit and clear() can call it without
    // capturing a stale reference.
    stopListeningRef.current = stopListening;

    const micSupported = !hideSpeechToTextButton && isSupportedBrowser && isSupportedLanguage;
    const micAvailable = !disableSpeechToText && micSupported;
    const micDisabled = disabled || sttError !== null;

    const handleMicClick = useCallback(() => {
      if (listeningRef.current) {
        stopListeningRef.current();
      } else {
        // Ignore restart clicks while the recognition session is winding down.
        if (status === SpeechRecognitionStatus.SoundEnded) return;
        startListening();
      }
    }, [startListening, status]);

    React.useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      clear: () => {
        if (listeningRef.current) {
          stopListeningRef.current();
        }
        setInterimTranscript('');
        if (value !== undefined) {
          // In controlled mode the value prop overrides any DOM mutation on re-render.
          // Callers must reset their own state instead of calling clear().
          return;
        }
        if (textareaRef.current) {
          textareaRef.current.value = '';
          updateHeight(textareaRef.current);
        }
        setUncontrolledValue('');
        if (attachmentPreviewsRef.current.length > 0) {
          setAttachmentPreviews([]);
          onAttachmentsChange?.([]);
        }
      },
    }));

    // Revoke all object URLs when the component unmounts to avoid memory leaks.
    useEffect(
      () => () => {
        createdUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      },
      []
    );

    const updateHeight = useCallback(
      (el: HTMLTextAreaElement) => {
        el.style.height = 'auto';
        const newHeight = Math.min(el.scrollHeight, maxTextareaHeight);
        el.style.height = `${newHeight}px`;

        // Sync the hidden span so we can measure the text's natural single-line width.
        if (textWidthMeasureRef.current) {
          textWidthMeasureRef.current.textContent = el.value;
        }

        // Guard: element not yet laid out — leave state unchanged.
        if (el.clientWidth !== 0) {
          const textWidth = textWidthMeasureRef.current?.scrollWidth ?? 0;

          // Compute the next multi-line state and perform the ref mutation outside the
          // setter so the updater function remains pure (React Strict Mode double-invokes
          // updaters to surface side-effects, so mutations inside setters are unsafe).
          let nextIsMultiLine: boolean;
          if (!isMultiLine) {
            // Currently single-line: save the narrow textarea width so that when the
            // component later switches to multi-line (and el.clientWidth becomes the full
            // width), we can still compare against the original narrow threshold.
            singleLineWidthRef.current = el.clientWidth;
            // Switch when text fills the line (before it wraps) or when an explicit
            // newline already made content taller than one line.
            nextIsMultiLine = textWidth >= el.clientWidth || el.scrollHeight > SINGLE_LINE_HEIGHT;
          } else {
            // Currently multi-line: compare against the saved *narrow* width, not
            // el.clientWidth (which is the full textarea width here). Updating the ref
            // to el.clientWidth would raise the threshold and cause text that only fills
            // the narrow slot to snap back to single-line on every keystroke.
            nextIsMultiLine = textWidth >= singleLineWidthRef.current || el.scrollHeight > SINGLE_LINE_HEIGHT;
          }

          // Only allow transitioning to multi-line, never back — prevents layout
          // oscillation when leadingContent shifts the available textarea width.
          if (nextIsMultiLine && !isMultiLine) {
            setIsMultiLine(true);
          }
        }
      },
      [isMultiLine, maxTextareaHeight]
    );

    useEffect(() => {
      if (textareaRef.current) {
        updateHeight(textareaRef.current);
      }
    }, [value, uncontrolledValue, interimTranscript, updateHeight]);

    // Char-limit screen-reader debounce — mirrors the CDS CharacterLimit component.
    // Updates the live region only after the user pauses typing (1 s) so the screen
    // reader can complete each announcement uninterrupted. Announces only when crossing
    // key thresholds to avoid overwhelming users with continuous updates.
    useEffect(() => {
      // Skip the initial mount — nothing to announce before the user types.
      if (isCharLimitMountRef.current) {
        isCharLimitMountRef.current = false;
        return;
      }
      if (maxLength === undefined || remaining === undefined) return;

      const prev = prevRemainingRef.current;
      prevRemainingRef.current = remaining;

      if (prev !== undefined) {
        const ratio = remaining / maxLength;
        const prevRatio = prev / maxLength;

        // Fire on threshold crossings so fast typing that skips a threshold still
        // triggers an announcement: entering/leaving the ≤20% zone, or restoring to full.
        // `ratio <= 0.2` keeps the pending flag armed on every keystroke inside the danger
        // zone — not just on entry — so each typing pause within the zone produces a fresh
        // announcement with the updated count (same pattern as `remaining <= 0` for over-limit).
        const crossedThreshold = ratio === 1 || prevRatio > 0.2 !== ratio > 0.2 || ratio <= 0.2;

        if (crossedThreshold) {
          pendingCharLimitRef.current = true;
        }
      }

      if (!pendingCharLimitRef.current) return;

      if (charLimitTimerRef.current) clearTimeout(charLimitTimerRef.current);
      charLimitTimerRef.current = setTimeout(() => {
        setAnnouncedRemaining(latestRemainingRef.current);
        pendingCharLimitRef.current = false;
        charLimitTimerRef.current = null;
      }, 1000);
    }, [maxLength, remaining]);

    // Clean up any pending char-limit timer on unmount.
    useEffect(() => {
      return () => {
        if (charLimitTimerRef.current) clearTimeout(charLimitTimerRef.current);
      };
    }, []);

    // Stop an active STT session when video (or other source) disables speech-to-text,
    // so the microphone does not capture audio that isn't the user's voice.
    useEffect(() => {
      if (disableSpeechToText && listeningRef.current) {
        stopListeningRef.current();
      }
    }, [disableSpeechToText]);

    // After files are added via the picker, move focus to the new attachment's remove
    // button so the user gets immediate tactile confirmation the file is attached.
    useEffect(() => {
      if (!pendingFocusAttachmentId.current) return;
      const el = attachmentDomRefs.current.get(pendingFocusAttachmentId.current);
      if (el) {
        el.querySelector<HTMLButtonElement>('button')?.focus();
        pendingFocusAttachmentId.current = null;
      }
    }, [attachmentPreviews]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // Block manual edits while voice input is active — the textarea value is
        // driven by the STT transcript during this period.
        if (listeningRef.current) return;
        updateHeight(e.currentTarget);
        if (value === undefined) {
          setUncontrolledValue(e.currentTarget.value);
        }
        onChange?.(e.currentTarget.value);
      },
      [onChange, updateHeight, value]
    );

    // Consolidates all send logic. The form submit event is used so that IME
    // composition (e.g. Japanese kanji selection) never accidentally triggers a
    // send — browsers do not fire submit during an active composition session.
    const handleSubmit = useCallback(
      (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!canSend) return;
        if (listeningRef.current) {
          stopListeningRef.current();
        }
        // Read from the controlled state value, not the DOM — the textarea DOM node
        // still contains currentValue + interimTranscript at this point because
        // React state updates (setInterimTranscript) are batched and haven't flushed yet.
        onSend?.(currentValue.trim());
        textareaRef.current?.focus();
      },
      [canSend, currentValue, onSend]
    );

    // Pressing Enter (without Shift, and not during IME composition) triggers a
    // form submission via requestSubmit() rather than calling onSend directly.
    // This ensures the isComposing guard is always respected: requestSubmit()
    // fires a proper submit event which browsers skip during composition, and
    // handleSubmit owns all the canSend validation in one place.
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        formRef.current?.requestSubmit();
      }
    }, []);

    // Shared logic for adding files from any source (file picker or paste).
    // Reads current previews from the ref so it is always fresh without being
    // listed as a stale-closure dependency.
    const addFiles = useCallback(
      (files: File[]) => {
        const newPreviews = files.map((file) => {
          const isImage = file.type.startsWith('image/');
          const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
          if (previewUrl) createdUrlsRef.current.push(previewUrl);
          const id = String(attachmentIdCounterRef.current);
          attachmentIdCounterRef.current += 1;
          return { id, file, previewUrl };
        });
        const next = [...attachmentPreviewsRef.current, ...newPreviews];
        setAttachmentPreviews(next);
        onAttachmentsChange?.(next.map((p) => p.file));
      },
      [onAttachmentsChange]
    );

    const handleUploadClick = useCallback(() => fileInputRef.current?.click(), []);

    const handleFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = e.currentTarget;
        if (!files || files.length === 0) return;
        // Record the ID that the first new attachment will receive so the focus effect
        // can move focus to its remove button once it renders.
        pendingFocusAttachmentId.current = String(attachmentIdCounterRef.current);
        addFiles(Array.from(files));
        // Reset so the same file can be selected again.
        e.currentTarget.value = '';
      },
      [addFiles]
    );

    // Intercepts paste events that contain files (e.g. a copied image). Plain text
    // paste is unaffected — clipboardData.files is empty in that case.
    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        // While voice input is active and no attachment handler is registered, the
        // only meaningful paste is plain text — which handleChange would silently
        // discard. Prevent default explicitly so the browser does not insert stale
        // text into the STT-driven value.
        if (listeningRef.current && !onAttachmentsChange) {
          e.preventDefault();
          return;
        }
        if (!onAttachmentsChange) return;
        const pastedFiles = Array.from(e.clipboardData.files)
          .filter((f) => f.size > 0)
          .filter((f) => !accept || matchesAccept(f, accept));
        if (pastedFiles.length === 0) return;
        e.preventDefault();
        addFiles(pastedFiles);
      },
      [onAttachmentsChange, accept, addFiles]
    );

    const handleDragOver = useCallback(
      (e: React.DragEvent<HTMLFormElement>) => {
        if (!onAttachmentsChange) return;
        e.preventDefault();
        setIsDraggingOver(true);
      },
      [onAttachmentsChange]
    );

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLFormElement>) => {
      // Only clear when the pointer truly leaves the form, not when it moves between children.
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent<HTMLFormElement>) => {
        if (!onAttachmentsChange) return;
        e.preventDefault();
        setIsDraggingOver(false);
        const droppedFiles = Array.from(e.dataTransfer.files)
          .filter((f) => f.size > 0)
          .filter((f) => !accept || matchesAccept(f, accept));
        if (droppedFiles.length === 0) return;
        addFiles(droppedFiles);
      },
      [onAttachmentsChange, accept, addFiles]
    );

    const handleRemovePreview = useCallback(
      (id: string) => {
        const item = attachmentPreviewsRef.current.find((p) => p.id === id);
        if (item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
          createdUrlsRef.current = createdUrlsRef.current.filter((u) => u !== item.previewUrl);
        }
        const next = attachmentPreviewsRef.current.filter((p) => p.id !== id);
        setAttachmentPreviews(next);
        onAttachmentsChange?.(next.map((p) => p.file));
      },
      [onAttachmentsChange]
    );

    const isMultiLineLayout = isMultiLine || multiLineLayout;

    const handleFormPointerDown = useCallback(() => {
      formRef.current?.setAttribute('data-pointer-focus', '');
    }, []);

    const handleFormBlurCapture = useCallback((e: React.FocusEvent<HTMLFormElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        formRef.current?.removeAttribute('data-pointer-focus');
      }
    }, []);

    // Clear pointer-focus on Tab so the ring appears when the user keyboard-navigates
    // within the form after a mouse click (blur stays inside, so blurCapture won't fire).
    const handleFormKeyDownCapture = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
      if (e.key === 'Tab') {
        formRef.current?.removeAttribute('data-pointer-focus');
      }
    }, []);

    const stopLabel = _t('Stop');
    const sendLabel = _t('Send message');
    const isTextareaReadOnly = disabled || listening;

    const uploadButton = onAttachmentsChange ? (
      <>
        <input
          ref={fileInputRef}
          accept={accept}
          className={classes.hiddenFileInput}
          multiple
          type="file"
          onChange={handleFileInputChange}
        />
        <IconButton
          aria-label={_t('Attach file')}
          icon={
            <span css={styles.scaledIcon}>
              <AddIcon />
            </span>
          }
          size="small"
          tooltip={_t('Attach file')}
          type="button"
          variant="ghostSecondary"
          onClick={handleUploadClick}
        />
      </>
    ) : null;

    const sendButton = isStreaming ? (
      <IconButton
        aria-label={stopLabel}
        className={classes.sendButton}
        disabled={disabled}
        icon={<StopFilledIcon />}
        size="small"
        tooltip={stopLabel}
        type="button"
        variant="primary"
        onClick={onStop}
      />
    ) : (
      <IconButton
        aria-label={sendLabel}
        className={classes.sendButton}
        disabled={!canSend}
        icon={<ArrowUpIcon />}
        size="small"
        tooltip={sendLabel}
        type="submit"
        variant="primary"
      />
    );

    // Show the visual char-limit counter when the user is in the last 20% of the
    // limit or has gone over, satisfying WCAG 2.2 SC 3.3.1 / 4.1.3 (status messages).
    const shouldAnnounceCharLimit =
      maxLength !== undefined &&
      remaining !== undefined &&
      (remaining <= Math.ceil(maxLength * 0.2) || isOverMaxLength);

    // Debounced values for the SR live region.
    const isAnnouncedOverMaxLength =
      maxLength !== undefined && announcedRemaining !== undefined && announcedRemaining < 0;
    const shouldAnnounceToSr =
      announcedRemaining !== undefined &&
      maxLength !== undefined &&
      (announcedRemaining <= Math.ceil(maxLength * 0.2) || isAnnouncedOverMaxLength);

    // Two separate elements mirror the CDS CharacterLimit pattern:
    //
    // 1. Visual span (aria-hidden): always reflects the live count so sighted users
    //    see an immediate update on every keystroke. Styled with charLimit/charLimitHidden
    //    so it only occupies flex space when visible.
    //
    // 2. SR live region (role="status"): shows announcedRemaining, which is updated only
    //    after the user pauses typing (1 s debounce) and only at threshold crossings.
    //    This prevents the screen reader from being interrupted mid-word on every keystroke,
    //    which was causing garbled announcements (e.g. reading adjacent page content).
    //    role="status" implies aria-live="polite" and aria-atomic="true" per the ARIA spec.
    //    Always in the DOM so the live region is registered on mount.
    //
    // No aria-describedby on the textarea: that would double-announce on focus + on update.
    const charLimitText = (
      <>
        {/* Visual display — hidden from screen readers, always shows the live count */}
        <span
          aria-hidden
          className={
            shouldAnnounceCharLimit
              ? classNames(classes.charLimit, isOverMaxLength && classes.charLimitError)
              : classes.charLimitHidden
          }
        >
          {shouldAnnounceCharLimit &&
            remaining !== undefined &&
            (isOverMaxLength ? (
              <FormattedMessage
                message={_t('{over, plural, =1 {# character too many} other {# characters too many}}')}
                over={Math.abs(remaining)}
              />
            ) : (
              <FormattedMessage
                message={_t('{remaining, plural, =1 {# character remaining} other {# characters remaining}}')}
                remaining={remaining}
              />
            ))}
        </span>
        {/* Screen-reader live region — debounced, threshold-based */}
        <span className={classes.charLimitAnnouncement} role="status">
          {shouldAnnounceToSr &&
            announcedRemaining !== undefined &&
            maxLength !== undefined &&
            (isAnnouncedOverMaxLength ? (
              <FormattedMessage
                message={_t(
                  '{over, plural, =1 {You have # character too many} other {You have # characters too many}}'
                )}
                over={Math.abs(announcedRemaining)}
              />
            ) : (
              <FormattedMessage
                message={_t('{remaining, plural, =1 {# character remaining} other {# characters remaining}}')}
                remaining={announcedRemaining}
              />
            ))}
        </span>
      </>
    );

    const micTooltip = sttError
      ? _t('Speech-to-text is not available at the moment. Try again later or use another browser.')
      : listening
        ? _t('Stop microphone')
        : _t('Use microphone');
    const microphoneButton = micAvailable ? (
      <IconButton
        aria-label={micTooltip}
        disabled={disabled}
        aria-disabled={micDisabled || undefined}
        icon={listening ? <StopFilledIcon /> : <MicrophoneIcon />}
        size="small"
        tooltip={micTooltip}
        type="button"
        variant="ghostSecondary"
        onClick={micDisabled ? undefined : handleMicClick}
      />
    ) : disableSpeechToText && micSupported ? (
      // aria-disabled (not disabled) is intentional: the disabled prop sets pointer-events:none on
      // the CDS IconButton, which prevents the tooltip from showing. aria-disabled keeps the tooltip
      // reachable so users can see why the mic is unavailable (e.g. "Video is playing").
      <IconButton
        aria-label={speechToTextDisabledTooltip || _t('Use microphone')}
        aria-disabled
        icon={<MicrophoneIcon />}
        size="small"
        tooltip={speechToTextDisabledTooltip || _t('Use microphone')}
        type="button"
        variant="ghostSecondary"
      />
    ) : null;

    const actionRowContent = (
      <>
        {charLimitText}
        {trailingActions && <div className={classes.trailingActionsWrapper}>{trailingActions}</div>}
        {microphoneButton}
        {sendButton}
      </>
    );

    // Inline previews for locally selected files. Rendered before consumer-provided
    // contextAttachments so newly added items appear on the left.
    const allImages = attachmentPreviews.length > 0 && attachmentPreviews.every((p) => p.previewUrl !== undefined);
    const mediaSize = allImages && attachmentPreviews.length <= 4 ? 'default' : 'small';

    const internalPreviews = attachmentPreviews.map((preview) => {
      const domRef = (el: HTMLDivElement | null) => {
        if (el) attachmentDomRefs.current.set(preview.id, el);
        else attachmentDomRefs.current.delete(preview.id);
      };
      return preview.previewUrl ? (
        <ChatContextAttachment
          key={preview.id}
          ref={domRef}
          type="media"
          size={mediaSize}
          fileName={preview.file.name}
          onRemove={() => handleRemovePreview(preview.id)}
        >
          <img alt={preview.file.name} css={styles.previewImage} src={preview.previewUrl} />
        </ChatContextAttachment>
      ) : (
        <ChatContextAttachment
          key={preview.id}
          ref={domRef}
          type="metadata"
          label={preview.file.name}
          onRemove={() => handleRemovePreview(preview.id)}
        />
      );
    });

    const hasContextRow = internalPreviews.length > 0 || Boolean(contextAttachments);

    // The upload button lives in the leading-action slot so it always appears in
    // the bottom-left corner regardless of single-line vs. multi-line layout.
    const leadingContent =
      uploadButton || leadingAction ? (
        <div className={classes.leadingAction}>
          {uploadButton}
          {leadingAction}
        </div>
      ) : null;

    return (
      <form
        ref={formRef}
        className={classNames(
          classes.root,
          isMultiLineLayout && classes.multiLine,
          isStreaming && classes.streaming,
          isDraggingOver && classes.dragging
        )}
        css={getChatResponseFieldCss}
        data-testid={dataTestId}
        noValidate
        style={{ '--crf-max-textarea-height': `${maxTextareaHeight}px` } as React.CSSProperties}
        onBlurCapture={handleFormBlurCapture}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onKeyDownCapture={handleFormKeyDownCapture}
        onPointerDown={handleFormPointerDown}
        onSubmit={handleSubmit}
      >
        <div aria-hidden="true" className={classes.focusRing} />
        {/* Measures text natural width to detect line-fill before wrapping occurs. */}
        <span ref={textWidthMeasureRef} aria-hidden="true" className={classes.textMeasure} />
        {/* Announces drag-over state to screen reader users. Always in the DOM so the live region
          is registered on mount. role="status" implies aria-live="polite" + aria-atomic="true". */}
        <span className={classes.dragAnnouncement} role="status">
          {onAttachmentsChange && isDraggingOver ? _t('Drop to attach files') : ''}
        </span>
        {/* Announces streaming state changes. Always in the DOM so the live region is registered
          on mount before the first isStreaming transition. */}
        <span className={classes.streamingAnnouncement} role="status">
          {isStreaming ? _t('Generating response') : ''}
        </span>
        {/* Describes the character limit to screen readers on textarea focus via aria-describedby.
          Rendered only when maxLength is set; aria-describedby on the textarea is also gated. */}
        {maxLength !== undefined && (
          <span id={maxLengthDescriptionId} className={classes.maxLengthDescription}>
            {_t('Maximum #{maxLength} characters', { maxLength: maxLength })}
          </span>
        )}
        {/* Announces mic active/inactive state. Always in the DOM so the live region is registered
          on mount. Needed because readOnly alone is not announced by all SR/browser combos. */}
        <span className={classes.micAnnouncement} role="status">
          {listening ? _t('Microphone active. Text input paused.') : ''}
        </span>

        {hasContextRow && (
          <div className={classes.contextRow}>
            {internalPreviews}
            {contextAttachments}
          </div>
        )}

        <div className={classes.textRow}>
          {!isMultiLineLayout && leadingContent}
          <textarea
            ref={textareaRef}
            aria-label={ariaLabel}
            aria-describedby={maxLengthDescriptionId}
            autoFocus={autoFocus}
            className={classes.textarea}
            rows={1}
            value={currentValue + interimTranscript}
            aria-disabled={disabled}
            readOnly={isTextareaReadOnly}
            aria-readonly={isTextareaReadOnly}
            placeholder={placeholder}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            data-testid="chat-response-field-textarea"
          />
          {!isMultiLineLayout && <div className={classes.actionRow}>{actionRowContent}</div>}
        </div>

        {isMultiLineLayout && (
          <div className={classes.actionRow}>
            {leadingContent}
            <div className={classes.actionRowRight}>{actionRowContent}</div>
          </div>
        )}
      </form>
    );
  }
);

export default ChatResponseField;
